import React, { useState, useCallback, useEffect } from 'react';
import { NavLink, useHistory } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { signOutUser } from 'src/redux/actions/authActions';
import { PAGES } from 'src/constants/pages';
import dropdownSettings from 'src/assets/images/icons/dropdown_settings.svg';
import { header } from 'src/assets/locales/en/common.json';
import { ReactComponent as LogoutIcon } from 'src/assets/images/icons/logout.svg';
import { ReactComponent as HelpIcon } from 'src/assets/images/icons/aside_menu/menu_help.svg';
import { ACCOUNT_ROLES, DEFAULT_TRANSITION_TIMEOUT } from 'src/constants/globalConstants';
import { signOutUserHelper, clearAuthToken } from 'src/helpers/authHelpers';
import styles from './ProfileDropdown.module.scss';
import { ReactComponent as KeyIcon } from 'src/assets/images/icons/key.svg';
import { userRoleSelector } from 'src/redux/selectors';

const ProfileDropdown = (): JSX.Element => {
  const { push } = useHistory();
  const dispatch = useDispatch();
  const userRole = useSelector(userRoleSelector);

  const [isDropdownShown, setDropdownShown] = useState(false);

  const handleLogOut = async () => {
    try {
      await signOutUserHelper();
      clearAuthToken();
      dispatch(signOutUser());
      push(PAGES.SIGN_IN);
    } catch (error) {
      console.error('handleLogOut error: ', error);
    }
  };

  const onKeyPress = useCallback(
    ({ key }) => {
      if (isDropdownShown && key === 'Escape') setDropdownShown(false);
    },
    [isDropdownShown]
  );

  useEffect(() => {
    document.addEventListener('keydown', onKeyPress);
    return () => {
      document.removeEventListener('keydown', onKeyPress);
    };
  }, [onKeyPress]);

  return (
    <div className={styles.settingsBtn} onClick={() => setDropdownShown(true)}>
      <img src={dropdownSettings} alt={header.dropdownAlt} className="img-fluid" />

      <CSSTransition
        in={isDropdownShown}
        unmountOnExit
        timeout={DEFAULT_TRANSITION_TIMEOUT}
        classNames={{
          enter: styles.enter,
          enterActive: styles.enterActive,
          enterDone: styles.enterDone,
          exit: styles.exit,
          exitActive: styles.exitActive,
          exitDone: styles.exitDone
        }}
      >
        <ClickAwayListener onClickAway={() => setDropdownShown(false)}>
          <div className={styles.dropdown}>
            <NavLink
              exact
              className={cn(styles.menuLink, styles.menuItem)}
              activeClassName={styles.menuLinkActive}
              to={PAGES.HELP_CENTER}
            >
              <div className={styles.menuLinkContent}>
                <div className={styles.iconWrapper}>
                  <HelpIcon className={cn(styles.icon, 'img-fluid')} />
                </div>

                <span className={styles.linkTitle}>Help Center</span>
              </div>
            </NavLink>

            {userRole === ACCOUNT_ROLES.USER && (
              <NavLink
                exact
                className={cn(styles.menuLink, styles.menuItem)}
                activeClassName={styles.menuLinkActive}
                to={PAGES.API_SETTINGS}
              >
                <div className={styles.menuLinkContent}>
                  <div className={styles.iconWrapper}>
                    <KeyIcon className={cn(styles.icon, 'img-fluid')} />
                  </div>

                  <span className={styles.linkTitle}>API settings</span>
                </div>
              </NavLink>
            )}

            <div className={styles.menuItem} onClick={handleLogOut}>
              <div className={styles.menuLinkContent}>
                <div className={styles.iconWrapper}>
                  <LogoutIcon className={cn(styles.icon, 'img-fluid')} />
                </div>

                <span className={styles.linkTitle}>Log Out</span>
              </div>
            </div>
          </div>
        </ClickAwayListener>
      </CSSTransition>
    </div>
  );
};

export default ProfileDropdown;
